/**
 * Sentry client-side noise filters.
 *
 * Everything here exists to keep third-party code from paging #engineering-jeff
 * with errors that are not ours:
 *
 * 1. Crypto-wallet browser extensions (2026-07-03, PROD-7/PROD-8): MetaMask-style
 *    extensions throw EIP-1193 rejections ({code: 4001, "User rejected the
 *    request."}) as unhandled promise rejections on our page. The rejected value
 *    is a plain object with no parseable frames, so Sentry's server-side
 *    browser-extension inbound filter can't catch it.
 *
 * 2. Intercom messenger widget (2026-07-03, PROD-9): Intercom's own bundles
 *    (js.intercomcdn.com — webpack + Redux internally) threw "Minified Redux
 *    error #3" from their tooltip middleware. Our SDK wraps addEventListener,
 *    so Intercom's unhandled exception got attributed to our release. Zero
 *    frames of our code involved.
 *
 * Rule of thumb: an exception whose stack contains no first-party frame is not
 * our bug and must not alert.
 */

/** Third-party script origins whose frames we never want to alert on. */
export const denyUrls: RegExp[] = [
  // Browser extensions
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
  /chrome-extension:\/\//i, // also match when embedded mid-URL in frame paths
  // Intercom messenger widget
  /js\.intercomcdn\.com/i,
  /widget\.intercom\.io/i,
]

export const ignoreErrors: (RegExp | string)[] = [
  /User rejected the request/i, // EIP-1193 code 4001 (wallet popup dismissed)
  /Object captured as promise rejection with keys: code, message(, stack)?$/,
]

type FrameLike = { abs_path?: string; filename?: string }
type EventLike = {
  exception?: { values?: { stacktrace?: { frames?: FrameLike[] } }[] }
}
type HintLike = { originalException?: unknown }

/** Frames from our own Sentry SDK wrapper — neutral, ignore when classifying. */
const SDK_FRAME = /@sentry[/\\]/

const isThirdPartyUrl = (url: string) => denyUrls.some((re) => re.test(url))

/**
 * Drop events that are provably not ours:
 * - EIP-1193 code-4001 wallet-rejection objects (no frames to match on)
 * - exceptions whose every meaningful frame comes from a third-party origin
 *   (denyUrls only checks the topmost frame, which is sometimes our SDK wrapper)
 */
export function beforeSend<E extends EventLike>(event: E, hint: HintLike): E | null {
  const orig = hint?.originalException as { code?: unknown } | undefined
  if (orig && typeof orig === 'object' && orig.code === 4001) return null

  const frames =
    event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? []
  const meaningful = frames
    .map((f) => f.abs_path || f.filename || '')
    .filter((url) => url && !SDK_FRAME.test(url))
  if (meaningful.length > 0 && meaningful.every(isThirdPartyUrl)) return null

  return event
}
